import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import showToast from '@Common/toast/showToast';

import {
  JiraProject,
  promptAnalyzer,
  GPTClassification,
  Assigned,
} from '@Models/types';
import { DropResult } from 'react-beautiful-dnd';
import { TokenCountData } from '@Components/analyzer/views/token/TokenCount';
import { useAuth } from './CognitoAuthProvider';
import { jiraAPI } from '@Lib/axios/jira';
import { analyzerAiAPI } from '@Lib/axios/analyzer';
import { jiraAnalyzerAPI } from '@Lib/axios/jiraAnalyzer';

interface AnalyzerContextProps {
  // jira props
  projects: JiraProject[];
  fetchProjects: () => Promise<void>;
  selectedProject: JiraProject | null;
  setSelectedProject: (project: JiraProject | null) => void;
  selectedProjects: JiraProject[];
  setSelectedProjects: (projects: JiraProject[]) => void;
  loadingProj: boolean;
  setLoadingProj: (isLoading: boolean) => void;

  filteredIssues: Assigned[];
  setFilteredIssues: (issues: Assigned[]) => void;
  taggedIssues: Assigned[];
  setTaggedIssues: (issues: Assigned[]) => void;
  loadingIssues: boolean;
  setLoadingIssues: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIssue: Assigned | null;
  setSelectedIssue: (record: Assigned | null) => void;

  // others
  search: string;
  setSearch: (query: string) => void;
  // orchestration and analysis actions
  classifyRecords: (records: Assigned[]) => Promise<Assigned[]>;
  handleIdentify: () => Promise<void>;
  cancelProcess: () => void;
  // ticket and tag management
  handleTagTicket: (record: Assigned) => void;
  handleSaveTagged: () => Promise<void>;
  handleClearTagged: () => void;
  onDragEnd: (result: DropResult) => void;
  // filter and ui
  filterRecordsByTime: (records: Assigned[]) => Assigned[];
  sortTickets: (tickets: Assigned[]) => Assigned[];
  sortOption: string;
  setSortOption: (option: string) => void;
  handleItemClick: (record: Assigned) => void;

  tokenData: TokenCountData | null;
  promptOptions: promptAnalyzer[];
  setPromptOptions: (prompt: promptAnalyzer[]) => void;
  selectedPrompt: promptAnalyzer | null;
  setSelectedPrompt: (prompt: promptAnalyzer | null) => void;

  showAddPrompt: boolean;
  setShowAddPrompt: (show: boolean) => void;
  showItemModal: boolean;
  setShowItemModal: (show: boolean) => void;
  showViewPrompt: boolean;
  setShowViewPrompt: (show: boolean) => void;
  showFilter: boolean;
  setShowFilter: (show: boolean) => void;
  sortDropdownOpen: boolean;
  setSortDropdownOpen: (show: boolean) => void;

  // FilterPodsProps
  selectedDateFilter: string;
  setSelectedDateFilter: (value: string) => void;
  filterStartDate: Date | null;
  setFilterStartDate: (date: Date | null) => void;
  filterEndDate: Date | null;
  setFilterEndDate: (date: Date | null) => void;

  showCancel: boolean;
  setShowCancel: (show: boolean) => void;
  showSuggestions: boolean;
  setShowSuggestions: (show: boolean) => void;

  // confidence dropdowns
  isHighOpen: boolean;
  setIsHighOpen: (show: boolean) => void;
  isMidOpen: boolean;
  setIsMidOpen: (show: boolean) => void;
  isLowOpen: boolean;
  setIsLowOpen: (show: boolean) => void;

  // loading
  globalLoading: boolean;
  setGlobalLoading: (isLoading: boolean) => void;
  tagLoading: boolean;
  setTagLoading: (isLoading: boolean) => void;

  // Additional handlers and computed values
  removeSelectedProject: (key: string) => void;
  suggestions: Assigned[];
  highIssues: Assigned[];
  midIssues: Assigned[];
  lowIssues: Assigned[];
  setTokenData: React.Dispatch<React.SetStateAction<TokenCountData | null>>;
  cancelRef: React.MutableRefObject<boolean>;
}

// create analyzer context and context provider
const AnalyzerContext = createContext<AnalyzerContextProps | undefined>(
  undefined
);

export const AnalyzerContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 30);

  // jira
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<JiraProject | null>(
    null
  );
  const [selectedProjects, setSelectedProjects] = useState<JiraProject[]>([]);
  const [loadingProj, setLoadingProj] = useState<boolean>(false);

  const [filteredIssues, setFilteredIssues] = useState<Assigned[]>([]);
  const [taggedIssues, setTaggedIssues] = useState<Assigned[]>([]);
  const [loadingIssues, setLoadingIssues] = useState<boolean>(false);
  const [selectedIssue, setSelectedIssue] = useState<Assigned | null>(null);

  const [search, setSearch] = useState<string>('');
  const [sortOption, setSortOption] = useState<string>('confidenceDesc');
  const [promptOptions, setPromptOptions] = useState<promptAnalyzer[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<promptAnalyzer | null>(
    null
  );

  const [showAddPrompt, setShowAddPrompt] = useState<boolean>(false);
  const [showItemModal, setShowItemModal] = useState<boolean>(false);
  const [showViewPrompt, setShowViewPrompt] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);

  const [selectedDateFilter, setSelectedDateFilter] =
    useState<string>('between');
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(
    defaultStartDate
  );
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(
    defaultEndDate
  );

  const [showCancel, setShowCancel] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [isHighOpen, setIsHighOpen] = useState<boolean>(true);
  const [isMidOpen, setIsMidOpen] = useState<boolean>(true);
  const [isLowOpen, setIsLowOpen] = useState<boolean>(true);
  const [globalLoading, setGlobalLoading] = useState<boolean>(false);
  const [tagLoading, setTagLoading] = useState<boolean>(false);

  const [tokenData, setTokenData] = useState<TokenCountData | null>(null);

  const cancelRef = useRef(false);

  const { logout } = useAuth();
  const jira = jiraAPI(logout);
  const analyzerAi = analyzerAiAPI(logout);
  const jiraAnalyzer = jiraAnalyzerAPI(logout);

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  interface TicketInput {
    reference_number: string;
    title: string;
    description: string;
    tags: (string | { name: string })[];
    comments: string[];
  }

  const fetchProjects = async () => {
    const projects = await jira.getProjects();
    setProjects(projects || []);
  };

  // Classification logic
  const classifyRecords = async (records: Assigned[]): Promise<Assigned[]> => {
    if (!records || records.length === 0) return records;
    const chunkSize = 5;
    let classifications: GPTClassification[] = [];
    let totalPromptTokens = 0;
    for (let i = 0; i < records.length; i += chunkSize) {
      if (cancelRef.current) break;
      const chunk = records.slice(i, i + chunkSize);
      const ticketsChunk: TicketInput[] = chunk.map((record) => ({
        reference_number: record.reference_num,
        title: record.name,
        description: record.description || '',
        tags: record.tags || [],
        comments: record.comments || [],
      }));
      try {
        const response = (await analyzerAi.classifyTickets(
          ticketsChunk,
          selectedPrompt
        )) as {
          classifications: GPTClassification[];
          promptTokens: number;
        };
        classifications = classifications.concat(response.classifications);
        totalPromptTokens += response.promptTokens;
        await delay(500);
      } catch (error) {
        console.error('Error classifying chunk:', error);
        const errorClassifications: GPTClassification[] = chunk.map(
          (record) => ({
            reference_number: record.reference_num,
            error: 'Classification failed',
          })
        );
        classifications = classifications.concat(errorClassifications);
      }
    }
    setTokenData((prev) => ({
      promptTokens: (prev?.promptTokens || 0) + totalPromptTokens,
    }));
    return records.map((record) => {
      const classification = classifications.find(
        (c: GPTClassification) => c.reference_number === record.reference_num
      );
      return { ...record, gptClassification: classification };
    });
  };

  // Adapter: normalize JiraAssigned → Assigned so classifyRecords and UI work unchanged
  const normalizeJiraIssue = (
    issue: import('@Models/types').JiraAssigned
  ): Assigned => {
    const normalized: Assigned = {
      id: issue.id,
      reference_num: issue.key,
      name: issue.summary,
      created_at: issue.created,
      due_date: issue.duedate ?? undefined,
      tags: issue.labels,
      comments: issue.comment?.map((c) =>
        typeof c.body === 'string' ? c.body : ''
      ),
      // Plain text description — used by classifyRecords (AI classifier).
      description: issue.adfDescription,
      status: issue.status,
      record_type: issue.issuetype,
      url: issue.url,
      workflow_status: issue.statusCategory
        ? {
            id: String(issue.statusCategory.id),
            name: issue.statusCategory.name,
            position: 0,
            complete: false,
            color: issue.statusCategory.colorName,
          }
        : undefined,
    };

    // Carry htmlDescription (ADF → HTML with inlined base64 media) forward so
    // JiraItemModal can render it with dangerouslySetInnerHTML.
    (normalized as Assigned & { htmlDescription: string }).htmlDescription =
      issue.description ?? '';

    // Carry attachment[] forward so JiraItemModal can display them.
    if (issue.attachment?.length) {
      (
        normalized as Assigned & { attachment: typeof issue.attachment }
      ).attachment = issue.attachment;
    }

    return normalized;
  };

  // Helper function to fetch issues from a single Jira Project
  const fetchProjectRecords = async (prefix: string) => {
    const maxResults = 50;
    let nextPageToken: string | undefined;
    let pageCount = 0;
    const MAX_PAGES = 100;

    // JQL date filter based on selectedDateFilter, filterStartDate, and filterEndDate
    const formatDate = (date: Date) => date.toISOString().split('T')[0];
    let dateFilter = '';
    if (selectedDateFilter === 'Entire Pod') {
      dateFilter = '';
    } else if (selectedDateFilter === 'before' && filterEndDate) {
      dateFilter = `created < "${formatDate(filterEndDate)}"`;
    } else if (selectedDateFilter === 'after' && filterStartDate) {
      dateFilter = `created > "${formatDate(filterStartDate)}"`;
    } else if (
      selectedDateFilter === 'between' &&
      filterStartDate &&
      filterEndDate
    ) {
      dateFilter = `created >= "${formatDate(filterStartDate)}" AND created <= "${formatDate(filterEndDate)}"`;
    }

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (cancelRef.current) break;
      if (++pageCount > MAX_PAGES) break;

      const {
        issues: chunk,
        nextPageToken: newToken,
        isLast,
      } = await jiraAnalyzer.getAnalyzerIssues(
        prefix,
        maxResults,
        dateFilter,
        nextPageToken
      );

      if (cancelRef.current) break;
      if (!chunk.length) break;

      const normalized = chunk.map(normalizeJiraIssue);
      console.log('NORMALIZED ISSUES ', normalized);

      const classified = await classifyRecords(normalized);
      if (cancelRef.current) break;

      setFilteredIssues((prev) => {
        const merged = [...prev];
        classified.forEach((record) => {
          const isTagged = taggedIssues.some(
            (tagged) => tagged.id === record.id
          );
          const isInFiltered = merged.find((r) => r.id === record.id);

          if (!isTagged && !isInFiltered) {
            merged.push(record);
          }
        });
        return merged;
      });

      nextPageToken = newToken;
      await delay(1000);

      if (isLast) break;
      if (cancelRef.current) break;
    }
  };

  // Main identify function — Jira only
  const handleIdentify = async () => {
    if (selectedProjects.length === 0) {
      showToast.info('Please select at least one Jira project first');
      return;
    }
    cancelRef.current = false;
    setGlobalLoading(true);
    setLoadingIssues(true);
    setFilteredIssues([]);
    showToast.info('This might take a few moments to retrieve issues...');
    try {
      await Promise.all(
        selectedProjects.map(async (project) => {
          await fetchProjectRecords(project.key || '');
        })
      );
    } catch (error) {
      console.error('Error during identification:', error);
    } finally {
      setLoadingIssues(false);
      setGlobalLoading(false);
      showToast.success('Identification finished.');
    }
  };

  const cancelProcess = () => {
    cancelRef.current = true;
    setLoadingIssues(false);
    setGlobalLoading(false);
    console.log('Process cancelled');
  };

  // Filter records by time (kept for potential future use / filterRecordsByTime contract)
  const filterRecordsByTime = (records: Assigned[]): Assigned[] => {
    return records.filter((record) => {
      const recordDate = record.created_at ? new Date(record.created_at) : null;
      if (!recordDate) return false;
      if (selectedDateFilter === 'Entire Pod') return true;
      if (selectedDateFilter === 'before' && filterEndDate) {
        return recordDate < filterEndDate;
      }
      if (selectedDateFilter === 'after' && filterStartDate) {
        return recordDate > filterStartDate;
      }
      if (
        selectedDateFilter === 'between' &&
        filterStartDate &&
        filterEndDate
      ) {
        return recordDate >= filterStartDate && recordDate <= filterEndDate;
      }
      return true;
    });
  };

  // Sort tickets
  const sortTickets = useCallback(
    (tickets: Assigned[]) => {
      const sorted = [...tickets];
      switch (sortOption) {
        case 'confidenceAsc':
          sorted.sort(
            (a, b) =>
              (a.gptClassification?.confidence_score || 0) -
              (b.gptClassification?.confidence_score || 0)
          );
          break;
        case 'confidenceDesc':
          sorted.sort(
            (a, b) =>
              (b.gptClassification?.confidence_score || 0) -
              (a.gptClassification?.confidence_score || 0)
          );
          break;
        case 'pod':
          sorted.sort((a, b) => a.reference_num.localeCompare(b.reference_num));
          break;
        case 'timeframeNewest':
          sorted.sort(
            (a, b) =>
              new Date(b.created_at || '').getTime() -
              new Date(a.created_at || '').getTime()
          );
          break;
        case 'timeframeOldest':
          sorted.sort(
            (a, b) =>
              new Date(a.created_at || '').getTime() -
              new Date(b.created_at || '').getTime()
          );
          break;
        default:
          break;
      }
      return sorted;
    },
    [sortOption]
  );

  // Tag management
  const handleTagTicket = (record: Assigned) => {
    setTaggedIssues((prev) => {
      if (prev.find((r) => r.id === record.id)) return prev;
      return [...prev, record];
    });
    setFilteredIssues((prev) => prev.filter((r) => r.id !== record.id));
    setShowItemModal(false);
  };

  const handleSaveTagged = async () => {
    if (taggedIssues.length === 0) {
      showToast.info('Please tag tickets first');
      return;
    }
    setTagLoading(true);
    try {
      const result = await jiraAnalyzer.tagTickets(taggedIssues);
      console.log('Tagged result:', result);
      setTaggedIssues([]);
      showToast.success('Tickets successfully tagged.');
    } catch (error) {
      console.error('Error saving tagged tickets:', error);
      showToast.error('Error tagging tickets.');
    }
    setTagLoading(false);
  };

  const handleClearTagged = () => {
    if (taggedIssues.length === 0) {
      showToast.info('Please tag tickets first');
      return;
    }
    setFilteredIssues((prev) => [...prev, ...taggedIssues]);
    setTaggedIssues([]);
    showToast.info('Cleared Tagged');
  };

  const handleItemClick = (record: Assigned) => {
    setSelectedIssue(record);
    setShowItemModal(true);
  };

  const removeSelectedProject = (key: string) => {
    setSelectedProjects(selectedProjects.filter((p) => p.key !== key));
  };

  // Search suggestions — based on filteredIssues for Jira
  const suggestions = useMemo(() => {
    if (!search.trim()) return [];
    const searchLower = search.toLowerCase();
    return filteredIssues
      .filter(
        (issue) =>
          issue.reference_num.toLowerCase().includes(searchLower) ||
          issue.name.toLowerCase().includes(searchLower)
      )
      .slice(0, 5);
  }, [search, filteredIssues]);

  const filteredIssuesBySearch = useMemo(() => {
    if (search.trim().length > 2) {
      const searchLower = search.toLowerCase();
      return filteredIssues.filter(
        (issue) =>
          issue.reference_num.toLowerCase().includes(searchLower) ||
          issue.name.toLowerCase().includes(searchLower)
      );
    }
    return filteredIssues;
  }, [filteredIssues, search]);

  const highIssues = useMemo(() => {
    const issues = filteredIssuesBySearch.filter(
      (issue) =>
        issue.gptClassification?.confidence_score !== undefined &&
        issue.gptClassification.confidence_score >= 70
    );
    return sortTickets(issues);
  }, [filteredIssuesBySearch, sortTickets]);

  const midIssues = useMemo(() => {
    const issues = filteredIssuesBySearch.filter(
      (issue) =>
        issue.gptClassification?.confidence_score !== undefined &&
        issue.gptClassification.confidence_score >= 30 &&
        issue.gptClassification.confidence_score < 70
    );
    return sortTickets(issues);
  }, [filteredIssuesBySearch, sortTickets]);

  const lowIssues = useMemo(() => {
    const issues = filteredIssuesBySearch.filter(
      (issue) =>
        !issue.gptClassification ||
        issue.gptClassification.confidence_score === undefined ||
        issue.gptClassification.confidence_score < 30
    );
    return sortTickets(issues);
  }, [filteredIssuesBySearch, sortTickets]);

  // Drag and drop
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;
    const filteredGroupIds = ['high', 'mid', 'low'];

    // Prevent moving between confidence groups
    if (
      filteredGroupIds.includes(source.droppableId) &&
      filteredGroupIds.includes(destination.droppableId) &&
      source.droppableId !== destination.droppableId
    ) {
      return;
    }

    // Move from confidence group to tagged
    if (
      filteredGroupIds.includes(source.droppableId) &&
      destination.droppableId === 'tagged'
    ) {
      let movedRecord: Assigned | undefined;
      if (source.droppableId === 'high') {
        movedRecord = highIssues[source.index];
      } else if (source.droppableId === 'mid') {
        movedRecord = midIssues[source.index];
      } else if (source.droppableId === 'low') {
        movedRecord = lowIssues[source.index];
      }
      if (!movedRecord) return;

      setFilteredIssues((prev) => prev.filter((r) => r.id !== movedRecord!.id));
      setTaggedIssues((prev) => {
        const newList = Array.from(prev);
        newList.splice(destination.index, 0, movedRecord!);
        return newList;
      });
      return;
    }

    // Move from tagged back to confidence group
    if (
      source.droppableId === 'tagged' &&
      filteredGroupIds.includes(destination.droppableId)
    ) {
      const movedRecord = taggedIssues[source.index];
      setTaggedIssues((prev) => {
        const newList = Array.from(prev);
        newList.splice(source.index, 1);
        return newList;
      });
      setFilteredIssues((prev) => [...prev, movedRecord]);
      return;
    }

    // Reorder within tagged
    if (
      source.droppableId === 'tagged' &&
      destination.droppableId === 'tagged'
    ) {
      const items = Array.from(taggedIssues);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      setTaggedIssues(items);
      return;
    }
  };

  const value: AnalyzerContextProps = {
    // Jira props
    projects,
    fetchProjects,
    selectedProject,
    setSelectedProject,
    selectedProjects,
    setSelectedProjects,
    loadingProj,
    setLoadingProj,
    filteredIssues,
    setFilteredIssues,
    taggedIssues,
    setTaggedIssues,
    loadingIssues,
    setLoadingIssues,
    selectedIssue,
    setSelectedIssue,

    // Others
    search,
    setSearch,
    classifyRecords,
    handleIdentify,
    cancelProcess,
    handleTagTicket,
    handleSaveTagged,
    handleClearTagged,
    onDragEnd,
    filterRecordsByTime,
    sortTickets,
    sortOption,
    setSortOption,
    handleItemClick,
    tokenData,
    promptOptions,
    setPromptOptions,
    selectedPrompt,
    setSelectedPrompt,
    showAddPrompt,
    setShowAddPrompt,
    showItemModal,
    setShowItemModal,
    showViewPrompt,
    setShowViewPrompt,
    showFilter,
    setShowFilter,
    sortDropdownOpen,
    setSortDropdownOpen,
    selectedDateFilter,
    setSelectedDateFilter,
    filterStartDate,
    setFilterStartDate,
    filterEndDate,
    setFilterEndDate,
    showCancel,
    setShowCancel,
    showSuggestions,
    setShowSuggestions,
    isHighOpen,
    setIsHighOpen,
    isMidOpen,
    setIsMidOpen,
    isLowOpen,
    setIsLowOpen,
    globalLoading,
    setGlobalLoading,
    tagLoading,
    setTagLoading,

    // Additional handlers and computed values
    removeSelectedProject,
    suggestions,
    highIssues,
    midIssues,
    lowIssues,
    setTokenData,
    cancelRef,
  };

  return (
    <AnalyzerContext.Provider value={value}>
      {children}
    </AnalyzerContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAnalyzerContext = () => {
  const context = useContext(AnalyzerContext);
  if (!context) {
    throw new Error(
      'useAnalyzerContext must be used within an AnalyzerContextProvider'
    );
  }
  return context;
};
